import { useLanguage } from "src/context/LanguageContext";
import { Button } from "src/components/ui/button";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UrlImage } from "../../../lib/api";
import { SettingsData } from "../../../lib/languages";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import UploadImageProfile from "../../../components/common/UploadImageProfile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

export default function ProfileDialog({
  open,
  onOpenChange,
  profileData,
  onSave,
  saving,
  setSaving,
  errors,
}) {
  const { language } = useLanguage();
  const t = SettingsData[language] || SettingsData["en"];

  const [formData, setFormData] = useState({
    name: "",
    user_name: "",
    image: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    role: "",
    is_parent: 0,
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (profileData)
      setFormData({
        name: profileData?.name,
        user_name: profileData?.user_name,
        image: profileData?.image,
        email: profileData?.email,
        phone: profileData?.phone,
        role: profileData?.role,
        is_parent: profileData.is_parent,
      });
  }, [profileData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e) => {
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      e.preventDefault();
      return;
    }
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[500px] max-h-[90vh] overflow-auto"
        dir={language === "en" ? "ltr" : "rtl"}
      >
        <DialogHeader>
          <DialogTitle>
            {profileData?.id ? "Edit Profile" : "Add Profile"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="flex items-center justify-center">
              <UploadImageProfile
                name="users"
                disabled={false}
                imageUrl={formData.image && `${UrlImage}${formData.image}`}
                onImageUploaded={(url) =>
                  setFormData({ ...formData, image: url })
                }
                isUploading={(uploading) => setSaving(uploading)}
              />
              {errors.image && (
                <p className="text-red-500 text-sm">{errors.image}</p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div className="grid gap-2">
                <label htmlFor="name">{t.name || "Name"}</label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                />
                {errors.name && (
                  <p className="text-red-500 text-sm">{errors.name}</p>
                )}
              </div>

              {/* Username */}
              <div className="grid gap-2">
                <label htmlFor="user_name">{t.username || "Username"}</label>
                <Input
                  id="user_name"
                  name="user_name"
                  autoComplete="off"
                  value={formData.user_name}
                  onChange={handleChange}
                  placeholder="johndoe"
                  required
                />
                {errors.user_name && (
                  <p className="text-red-500 text-sm">{errors.user_name}</p>
                )}
              </div>

              <div className="grid gap-2">
                <label htmlFor="password">Password</label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    autoComplete="off"
                    placeholder={"••••••••"}
                    type={showNewPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="grid gap-2">
                <label htmlFor="confirmPassword">
                  {t.confirmPassword || "Confirm Password"}
                </label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder={"••••••••"}
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    <span className="sr-only">Toggle password visibility</span>
                  </Button>
                </div>
              </div>

              {/* Email */}
              <div className="grid gap-2">
                <label htmlFor="email">{t.email || "Email"}</label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="off"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div className="grid gap-2">
                <label htmlFor="phone">{t.phone || "Phone"}</label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 234 567 8900"
                  required
                />
                {errors.phone && (
                  <p className="text-red-500 text-sm">{errors.phone}</p>
                )}
              </div>

              {/* Role */}
              <div className="grid gap-2">
                <label htmlFor="role">{t.role || "Role"}</label>
                <Select
                  id="role"
                  name="role"
                  value={formData.role || ""}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, role: value }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="teacher">Teacher</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                  </SelectContent>
                </Select>
                {errors.role && (
                  <p className="text-red-500 text-sm">{errors.role}</p>
                )}
              </div>

              {/* Is Parent */}
              <div className="grid gap-2">
                <label htmlFor="is_parent">{t.is_parent || "Is Parent"}</label>
                <Select
                  id="is_parent"
                  name="is_parent"
                  value={String(formData.is_parent || 0)}
                  onValueChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_parent: Number(value),
                    }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Yes</SelectItem>
                    <SelectItem value="0">No</SelectItem>
                  </SelectContent>
                </Select>
                {errors.is_parent && (
                  <p className="text-red-500 text-sm">{errors.is_parent}</p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className={"flex gap-2 justify-between flex-wrap"}>
            <DialogClose>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-fit"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {t.save || "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
